import React, { useContext } from 'react'
import { inject, observer } from 'mobx-react'
import IconButtonBase from '@material-ui/core/IconButton'
import type { LanguageLocale } from 'utils/utils'
import { formatPrice, isMobile, getLocaleStr, getTranslatedTextByKey, getSectionTitleForItem } from 'utils/utils'
import { StoreContext } from 'contexts/StoreContext'
import Big from 'big.js'
import EditIconBase from '../common/EditIcon'
import CartSummaryVariationsChoices from './CartSummaryVariationsChoices'
import styled from 'styled-components'
import TypographyPro from 'themes/TypographyPro'
import IconComponent from 'themes/IconComponent'
import { sendEnhancedEcommerceEvent, sendCustomEvent } from '../../utils/analytics/analytics'
import { CONSTANTS } from '../../utils/constants'
import { useRouter } from 'next/router'
import { useStores } from 'hooks/useStores'
import type { CartItem } from 'shared-types/Cart'
import type _Infra from 'mobx/Infra'
import type _Application from 'mobx/Application'
import { SnackbarStatus } from 'mobx/Infra/Infra.type'

const EEE = CONSTANTS.ANALYTICS.ENHANCED_ECOMMERCE_EVENTS
const TrashCan = () => <IconComponent asset="/icons/trashCan.svg" />

const IconButton = styled(IconButtonBase)`
	padding: 0;
`

const EditIcon = styled(EditIconBase)`
	width: 20px;
	height: 20px;

	& path {
		fill: var(--disable);
	}
`

const Container = styled.div`
	display: flex;
	justify-content: space-between;
`

const ItemQuantityTag = styled.span`
	mix-blend-mode: multiply;
	border-radius: 4px;
	height: 28px;
	width: 28px;
	display: flex;
	justify-content: center;
	align-items: center;
	font-weight: 600;
	margin-inline-end: 10px;
	background: transparent;
	border: solid 1px var(--primary);
`

const ItemTitle = styled(TypographyPro)`
	color: var(--strokeMenuTitle);
	display: flex;
	align-items: center;
`

const VariationsContainer = styled.div`
	margin: 10px 0;
`

const QuantityPriceContainer = styled.div`
	display: flex;
	align-items: center;
`

const ItemTitleAndPrice = styled.div`
	display: flex;
	flex: 1;
	justify-content: space-between;
`

const PriceText = styled(TypographyPro)`
	color: var(--inactive);
	${({ theme }) => theme.typography.BodyRegular};
`

const ActionButtonsContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: auto;
	margin-inline-start: 20px;
	padding: 3px 0 0;

	@media (max-width: 768px) {
		margin-inline-start: 15px;
	}
`

const HeaderContainer = styled.div`
	display: flex;
`

const SummaryItemContainer = styled.div`
	flex: 1;
`

const CommentsContainer = styled.div`
	margin-bottom: 10px;
`

const CommentsText = styled(TypographyPro)`
	color: var(--inactive);
	margin-bottom: 10px;
`

const ItemTotalPrice = styled.div`
	color: var(--font);
	${({ theme }) => theme.typography.BodyRegularHighlighted};
	display: flex;
	border-top: 1px solid var(--optionalBackground);
	padding-top: 10px;
	margin-top: 15px;
`

const IconButtonStyle = styled(IconButton)`
	svg {
		width: 20px;
		height: 20px;
	}
`

interface CartSummaryRowProps {
	prefferedLanguage?: LanguageLocale
	isCheckout: boolean
	cartItem: CartItem
	allowUpdate: boolean
	cartItemIdx: number
	Infra: typeof _Infra
	Application: typeof _Application
}

const CartSummaryRow = inject(
	'Infra',
	'Application'
)(
	observer((props: CartSummaryRowProps) => {
		const { store } = useContext(StoreContext)
		const rest = store.data
		const { Application, Infra, isCheckout, prefferedLanguage: preferredLanguage, cartItem, cartItemIdx } = props
		const locale: LanguageLocale = preferredLanguage ?? rest?.locale ?? 'en_US'
		const router = useRouter()
		const { cartStore } = useStores()

		const _fullStoreItem = rest?.items[cartItem.itemId]
		let _itemTitle = ''
		let itemQuantityPrice: Big

		if (_fullStoreItem) {
			_itemTitle = _fullStoreItem.title ? getLocaleStr(_fullStoreItem.title, locale) : ''
			itemQuantityPrice = new Big(cartItem.price).times(cartItem.quantity)
		}

		const deleteItem = () => {
			Infra.setNotification({
				// open: true,
				title: getTranslatedTextByKey('webviewFlow.confirmRemovalOf'),
				message: _itemTitle,
				cancelAction: () => {
					Infra.closeNotification()
				},
				okAction: async () => {
					await cartStore.removeItem(cartItem.itemId, cartItemIdx)
					if (cartStore.numberOfItems === 0 && (isMobile() || isCheckout)) {
						router.back()
					}

					Infra.showSnackbar({
						snackId: 'cart',
						message: `'${_itemTitle}' ${getTranslatedTextByKey('webviewFlow.removedFromYourCart')}`,
						status: SnackbarStatus.SUCCESS,
						isAttachedToElement: !isCheckout,
					})
					if (!rest) {
						return
					}
					sendEnhancedEcommerceEvent(
						EEE.sendRemoveFromCartEvent,
						{
							id: cartItem.itemId,
							itemTitle: _itemTitle,
							description: rest?.items[cartItem.itemId].description,
							price: props.cartItem.price,
							sectionTitle: getSectionTitleForItem(cartItem.itemId, rest.sections, rest.locale ?? 'en_US'),
							quantity: props.cartItem.quantity,
							total: Number(itemQuantityPrice.toString()), // can't use .toNumber() since it throws an error
						},
						{
							currency: rest.currency,
							countryCode: rest.countryCode,
						},
						rest,
						cartStore.getAnalyticsFields()
					)
				},
			})
		}

		const editItem = () => {
			sendCustomEvent({
				category: 'cart',
				action: 'edit',
				label: `${_itemTitle} - ${cartItem.itemId}`,
			})

			cartStore.openItemModal({
				item: cartItem,
				index: props?.cartItemIdx,
			})
		}

		const editable = cartItem.total > 0

		return (
			_fullStoreItem && (
				<Container>
					<SummaryItemContainer>
						<HeaderContainer>
							<ItemQuantityTag>
								<TypographyPro variant="BodyRegular" data-testid="cart-item-quantity">
									x{props.cartItem.quantity}
								</TypographyPro>
							</ItemQuantityTag>
							<ItemTitleAndPrice>
								<ItemTitle variant="h5" data-testid="cart-item-title">
									{_itemTitle}
								</ItemTitle>
								<QuantityPriceContainer>
									{/* Todo qunatity implementation will be here */}
									{/* <QuantityContainer> */}
									{/*	<AddIcon /> */}
									{/*	<TypographyPro variant="bodyRegular">x{props.cartItem.quantity}</TypographyPro> */}
									{/*	<RemoveIcon /> */}
									{/* </QuantityContainer> */}
									<PriceText variant="h5" data-testid="cart-item-price">
										{props.cartItem.price > 0 && formatPrice(props.cartItem.price, rest.currency, rest.countryCode, 1)}
									</PriceText>
								</QuantityPriceContainer>
							</ItemTitleAndPrice>
						</HeaderContainer>

						<VariationsContainer data-testid="variations-container">
							{cartItem.additionsNew?.[cartItem.itemId]?.variationsChoices && (
								/* possible recursion here for inner additions */
								<CartSummaryVariationsChoices
									itemId={cartItem.itemId}
									cartItem={cartItem}
									variationsChoices={cartItem.additionsNew[cartItem.itemId].variationsChoices}
								/>
							)}
						</VariationsContainer>

						<ItemTotalPrice id={`${Application.page}-page-item-total-price`} data-testid="cart-summary-item-total-price">
							{formatPrice(props.cartItem.total, rest.currency, rest.countryCode)}
						</ItemTotalPrice>

						{props?.cartItem?.comment?.text && (
							<CommentsContainer>
								<CommentsText variant="BodySmallHighlighted" component="span">
									Item comments:{' '}
									<TypographyPro variant="BodySmall" component="span">
										{props.cartItem.comment.text}
									</TypographyPro>
								</CommentsText>
							</CommentsContainer>
						)}
					</SummaryItemContainer>

					{props.allowUpdate && (
						<ActionButtonsContainer>
							<IconButton
								id={`${Application.page}-page-edit-item-icon`}
								edge="start"
								color="inherit"
								size="small"
								onClick={editItem}
								disabled={cartStore.loading}
								style={{ visibility: editable ? 'visible' : 'hidden' }}
								data-testid="cart-edit-item-button"
							>
								<EditIcon />
							</IconButton>
							<IconButtonStyle
								id={`${Application.page}-page-remove-item-icon`}
								color="inherit"
								onClick={deleteItem}
								disabled={cartStore.loading}
								data-testid="cart-delete-item-button"
							>
								<TrashCan />
							</IconButtonStyle>
						</ActionButtonsContainer>
					)}
				</Container>
			)
		)
	})
)

export default CartSummaryRow as React.FC<Omit<CartSummaryRowProps, 'Infra' | 'Application'>>
